// THE MAIL IN AN AI ANSWER — the `mail-threads` view the assistant mounts in
// its thread and a Fly page places as a section (workspace functions/
// assistantAppViews.js, the `views[]` contract).
//
// mailAiViews() is the PROVIDER, read from the mail EAGER slice; the view's
// component rides the lazy mail context (`fold: 'mail'` — the shell folds it
// through ensureMailContext before a card mounts). Mail is dark by default
// (the `mail` flag): the view declares the /mail page as its gate
// (`access.route`), so a workspace without mail — or a member without the
// app — is never offered it. One view, in the shell's list frame (workspace
// components/AssistantViewList.js):
//
//   mail-threads   the member's threads, newest first — a folder (inbox by
//                  default, or starred), only the unread ones, or a search;
//                  unread threads first; a row opens the thread in Mail
//
// The read is the mail list's own (GET /mail/threads through `mail.threads`
// list), into the view's OWN state — never root.mailThreads. Nothing here
// writes: opening a card marks nothing read.
//
// Frank note: el.call serializes each export on its own (FA204) — every export
// is self-contained.

export function mailAiViews() {
  return {
    app: {
      id: 'system/mail',
      navbar: [{ label: 'shell.app.mail', route: '/mail', icon: 'email' }],
      apps: [{ icon: 'email' }]
    },
    fold: 'mail',
    views: [
      {
        id: 'mail-threads',
        title: 'mail.page.title',
        describe:
          "The member's mail threads, newest first: a folder (inbox by default), only unread, or a search; unread first.",
        component: 'MailThreadsAnswerView',
        icon: 'email',
        hue: 'red',
        size: 'l',
        params: {
          folder: {
            type: 'enum',
            values: ['inbox', 'starred', 'sent', 'drafts', 'archive', 'spam', 'trash'],
            labels: {
              inbox: 'mail.folder.inbox',
              starred: 'mail.folder.starred',
              sent: 'mail.folder.sent',
              drafts: 'mail.folder.drafts',
              archive: 'mail.folder.archive',
              spam: 'mail.folder.spam',
              trash: 'mail.folder.trash'
            },
            default: 'inbox'
          },
          unread: { type: 'boolean', describe: 'only unread threads', default: false },
          search: { type: 'string', describe: 'words to search the mail for' },
          limit: { type: 'number', min: 1, max: 50, default: 12 }
        },
        link: { route: '/mail', query: {} },
        access: { route: '/mail' }
      }
    ]
  }
}

// The read: one page of threads for the folder / unread / search asked, in
// this workspace. `{ data: { threads, more }, failed }` — `more` when the
// list has a next page.
export async function mailThreadsAnswerLoad(el, params) {
  const host = el || this
  const p = params && typeof params === 'object' ? params : {}
  const root = (host && host.state && host.state.root) || {}
  const sdk = (host && host.sdk) || host.call('getSDK')
  if (!sdk || typeof sdk.execute !== 'function') return { data: { threads: [] }, failed: true }
  const FOLDERS = ['inbox', 'starred', 'sent', 'drafts', 'archive', 'spam', 'trash']
  const folder = FOLDERS.includes(p.folder) ? p.folder : 'inbox'
  const query = folder === 'starred' ? { starred: true } : { folder }
  if (p.unread === true || p.unread === 'true') query.unread = true
  const words = String(p.search || '').trim()
  if (words) query.q = words
  const limit = Math.max(1, Math.min(50, Math.floor(Number(p.limit)) || 12))
  const workspaceId = String((root.workspace && (root.workspace.id || root.workspace._id)) || '')
  const addr = (a) => ({
    name: (a && a.name) || '',
    email: String((a && a.email) || '').toLowerCase(),
    role: (a && a.role) || 'from'
  })
  try {
    const res = await sdk.execute('mail.threads', 'list', {
      ...query,
      limit,
      ...(workspaceId ? { workspaceId } : {})
    })
    const raw = Array.isArray(res) ? { rows: res } : res && typeof res === 'object' ? res : {}
    const rows = Array.isArray(raw.rows) ? raw.rows : Array.isArray(raw.threads) ? raw.threads : []
    // The thread fields the rows read (the list's own mapper, mailUtils
    // normalizeMailThread, keeps the same names).
    const threads = rows
      .filter((r) => r && (r.id || r._id) && !r.deleted)
      .map((r) => ({
        id: String(r.id || r._id),
        subject: r.subject || '',
        snippet: r.snippet || '',
        participants: Array.isArray(r.participants) ? r.participants.map(addr) : [],
        lastMessageAt: r.lastMessageAt ? String(r.lastMessageAt) : null,
        unreadCount: Number(r.unreadCount || 0)
      }))
    return { data: { threads, folder, more: !!(raw.cursor || raw.nextCursor) } }
  } catch (_) {
    return { data: { threads: [], folder }, failed: true }
  }
}

// The model: Unread (with its count) then Earlier — the folder's name labels
// a list with nothing unread. A row: the subject, "sender · when", the
// sender's initials; it opens the thread in Mail.
export function mailThreadsAnswerModel(data, params, failed) {
  const el = this
  const root = (el && el.state && el.state.root) || {}
  const d = data && typeof data === 'object' ? data : {}
  const p = params && typeof params === 'object' ? params : {}
  const say = (key) => String(el.call('polyglot', key))
  if (failed) return { groups: [], note: 'mail.list.loadFailed' }
  const threads = Array.isArray(d.threads) ? d.threads : []
  const me = String(root.currentUserEmail || '').toLowerCase()
  const folder = d.folder || 'inbox'
  const when = (iso) => {
    const ms = Date.parse(iso || '')
    if (!Number.isFinite(ms)) return ''
    const sameDay = new Date(ms).toDateString() === new Date().toDateString()
    return String(
      el.call(
        'localizedDate',
        new Date(ms),
        sameDay ? { hour: '2-digit', minute: '2-digit' } : { month: 'short', day: 'numeric' }
      ) || ''
    )
  }
  const row = (t) => {
    const role = folder === 'sent' || folder === 'drafts' ? 'to' : 'from'
    const list = Array.isArray(t.participants) ? t.participants : []
    const other =
      list.find((a) => a.role === role && a.email !== me) ||
      list.find((a) => a.email !== me) ||
      list[0] ||
      null
    const who = other ? other.name || other.email.split('@')[0] : say('mail.list.me')
    return el.call('assistantViewRow', {
      key: 't-' + String(t.id).replace(/[^a-zA-Z0-9_-]/g, ''),
      title: t.subject || say('mail.list.noSubject'),
      meta: [who, when(t.lastMessageAt)].filter(Boolean).join(' · '),
      initials: el.call('initials', who) || null,
      open: { call: 'openAppPage', arg: '/mail?thread=' + encodeURIComponent(t.id) }
    })
  }
  const unread = threads.filter((t) => t.unreadCount > 0)
  const rest = threads.filter((t) => !(t.unreadCount > 0))
  const groups = []
  if (unread.length) {
    groups.push(
      el.call('assistantViewGroup', {
        key: 'unread',
        label: say('mail.answer.unread').replace('{count}', String(unread.length)),
        rows: unread.map(row)
      })
    )
  }
  if (rest.length) {
    groups.push(
      el.call('assistantViewGroup', {
        key: 'earlier',
        label: unread.length ? say('mail.answer.earlier') : say('mail.folder.' + folder),
        rows: rest.map(row)
      })
    )
  }
  const note = groups.length
    ? ''
    : String(p.search || '').trim()
      ? 'mail.answer.noMatch'
      : 'mail.list.emptyFolder'
  return {
    groups,
    note,
    more: d.more ? { shown: threads.length, open: true } : null
  }
}
